import { Box, Tooltip, Typography } from '@mui/material';
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '@/shared/contexts/UserContext';
import { UserAvatar } from './UserAvatar';

interface AuthorUser {
  avatarColor?: string;
  firstName?: string;
  id?: number;
  lastName?: string;
  username?: string;
}

interface AuthorBadgeProps {
  userId?: number;
  user?: AuthorUser;
  onClick?: (e: React.MouseEvent) => void;
  subtitle?: string;
  size?: 'small' | 'medium' | 'large';
  showTooltip?: boolean;
  disableNavigation?: boolean;
  avatarOnly?: boolean;
}

const sizeConfig = {
  small: {
    avatarSize: 28,
    fontSize: '0.875rem',
    subtitleSize: '0.75rem',
    paddingY: '8px',
    paddingX: '12px',
    gap: 1,
  },
  medium: {
    avatarSize: 32,
    fontSize: '0.9375rem',
    subtitleSize: '0.8125rem',
    paddingY: '10px',
    paddingX: '14px',
    gap: 1.5,
  },
  large: {
    avatarSize: 40,
    fontSize: '1rem',
    subtitleSize: '0.875rem',
    paddingY: '12px',
    paddingX: '16px',
    gap: 2,
  },
};

export const AuthorBadge: React.FC<AuthorBadgeProps> = ({
  userId,
  user,
  onClick,
  subtitle = 'Click to engage with author',
  size = 'medium',
  showTooltip = true,
  disableNavigation = false,
  avatarOnly = false,
}) => {
  const navigate = useNavigate();
  const { user: currentUser } = useContext(UserContext);
  const config = sizeConfig[size];

  // Get display name for author
  const getAuthorName = () => {
    if (user?.username) {
      return user.username;
    }
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return 'Deal Retro Author';
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) {
      onClick(e);
    } else if (!disableNavigation && user?.username) {
      navigate(`/user/${user.username}`);
    }
  };

  const isOwnProfile = currentUser?.id === userId;
  const tooltipText = isOwnProfile ? 'This is your review' : `View ${getAuthorName()}'s profile`;

  const avatarColor = user?.avatarColor || '#3B6FD9';

  const badgeContent = (
    <Box
      onClick={handleClick}
      sx={
        avatarOnly
          ? {
              display: 'flex',
              alignItems: 'center',
              cursor: disableNavigation && !onClick ? 'default' : 'pointer',
            }
          : {
              display: 'flex',
              alignItems: 'center',
              gap: config.gap,
              py: config.paddingY,
              px: config.paddingX,
              borderRadius: 2,
              cursor: disableNavigation && !onClick ? 'default' : 'pointer',
              transition: 'all 0.2s ease',
              backgroundColor: 'grey.50',
              borderLeft: '3px solid',
              borderLeftColor: avatarColor,
              '&:hover':
                disableNavigation && !onClick
                  ? {}
                  : {
                      backgroundColor: 'grey.100',
                      transform: 'translateX(2px)',
                    },
            }
      }
    >
      <UserAvatar
        firstName={user?.firstName}
        lastName={user?.lastName}
        username={user?.username}
        avatarColor={avatarColor}
        size={config.avatarSize}
        fontSize={config.fontSize}
      />
      {!avatarOnly && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: config.fontSize,
              color: 'text.primary',
            }}
          >
            {getAuthorName()}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: config.subtitleSize,
              color: 'text.secondary',
            }}
          >
            {isOwnProfile ? 'This is your review' : subtitle}
          </Typography>
        </Box>
      )}
    </Box>
  );

  if (!userId) {
    return null;
  }

  if (showTooltip) {
    return (
      <Tooltip title={tooltipText} arrow>
        {badgeContent}
      </Tooltip>
    );
  }

  return badgeContent;
};

export default AuthorBadge;
